/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { Code } from './Code';
import { getCodeCss } from '../../../utils/getCodeCss/getCodeCss';
import { getCodeCssAngular } from '../../../utils/getCodeCssAngular/getCodeCssAngular';
import { getFilename } from '../../../utils/getFilename/getFilename';
import { HighlightCode } from '../../HighlightCode/HighlightCode';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { SECTION_NAME_CSS } from '../../../constants/snippetsSectionNames';
import { EditorSettingsContext } from '../../../contexts/EditorSettingsContext';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';
import { requiresExportCss } from '../../../utils/requiresExportCss/requiresExportCss';

export function Css( {
	isLoading,
	number,
	onCopyEvent
}: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology, selectedInstallMethod } = useContext( InstallationSettingsContext );
	const { editorPlugins, editorType, editorLanguage, toolbarOptions } = useContext( EditorSettingsContext );
	const editorOptions = { editorPlugins, editorType, editorLanguage, toolbarOptions };
	const cssCodeFn = selectedTechnology === 'angular' ? getCodeCssAngular : getCodeCss;
	const htmlFilename = getFilename( { type: 'html', selectedTechnology } );
	const cssFilename = getFilename( { type: 'css', selectedTechnology } );
	const exportCssFilename = getFilename( { type: 'exportCss', selectedTechnology } );
	const jsFilename = getFilename( { type: 'js', selectedTechnology } );

	const shouldExportCss = requiresExportCss( {
		editorPlugins: editorOptions.editorPlugins,
		selectedInstallMethod,
		selectedTechnology
	} );

	const cssLoaderFilename = selectedInstallMethod === 'cdn' ? htmlFilename : jsFilename;

	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_CSS } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_CSS }>
				<Text>
					Create a <Code>{ cssFilename }</Code> file in your project, and paste the code from below.
					{
						selectedTechnology === 'js' &&
							<span> Make sure it is in the same directory as <Code>{ cssLoaderFilename }</Code>.</span>
					}
					{
						shouldExportCss &&
							<span>
								{ ' ' }
								For proper export styling, include a copy of this file on your
								{ ' ' }
								assets server { '(' }which, in some frameworks, may be the <Code>public</Code>
								{ ' ' }
								directory{ ')' } as <Code>{ exportCssFilename }</Code>.
							</span>
					}
				</Text>
				<HighlightCode onCopyEvent={ onCopyEvent } lang={ 'css' } id={ 'snippet-css' }>
					{ cssCodeFn( { editorOptions, selectedInstallMethod } ) }
				</HighlightCode>
			</SnippetsSection>
		</SkeletonLoader>
	);
}
